import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { ArrowLeft, ShoppingBag, Minus, Plus, Heart } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useCart } from "./CartContext";
import { useWishlist } from "./WishlistContext";
import { useLanguage } from "./LanguageContext";
import { useAdminData } from "./admin/AdminDataContext";
import { localized } from "./admin/TranslationsPanel";
import { toast } from "sonner";
import { addToRecentlyViewed, RecentlyViewedSection } from "./RecentlyViewed";
import { ReviewSection } from "./ReviewSection";

const tagStyles: Record<string, string> = {
  new: "bg-[#6F7F63] text-white",
  limited: "bg-[#C7A76C] text-white",
  "sold out": "bg-[#6F675F] text-white",
};

export function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { t, lang } = useLanguage();
  const { products, siteConfig } = useAdminData();
  const wishlistEnabled = siteConfig.features?.wishlist !== false;
  const [selectedImage, setSelectedImage] = useState(0);
  const [qty, setQty] = useState(1);

  const product = products.find((p) => p.slug === slug);

  // Track recently viewed
  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product.id);
    }
  }, [product]);

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImage(0);
    setQty(1);
  }, [slug]);

  if (!product) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center">
        <h1
          className="text-[#2D2A26] tracking-[0.1em] mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "32px", fontWeight: 400 }}
        >
          {t("detail.not_found")}
        </h1>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 px-6 py-3 border border-[#6F7F63] text-[#6F7F63] tracking-[0.2em] uppercase rounded-full hover:bg-[#6F7F63] hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#6F7F63]"
          style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
        >
          <ArrowLeft size={14} />
          {t("detail.back_shop")}
        </button>
      </div>
    );
  }

  const related = product.relatedIds
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean) as typeof products;

  const handleAdd = () => {
    if (product.stock_quantity === 0) return;
    for (let i = 0; i < qty; i++) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images[0],
      });
    }
    toast.success(`${qty > 1 ? `${qty} \u00d7 ` : ""}${localized(product, "name", lang)} ${t("products.added")}`);
    setQty(1);
  };

  const handleWishlistToggle = () => {
    toggleWishlist(product.id);
    toast.success(isWishlisted(product.id) ? t("wishlist.removed") : t("wishlist.added"));
  };

  const isOutOfStock = product.stock_quantity === 0;
  const isLowStock = product.stock_quantity > 0 && product.stock_quantity <= product.low_stock_threshold;

  return (
    <div className="pt-24 pb-24">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <nav className="flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
          <Link
            to="/"
            className="text-[#D8D1C7] hover:text-[#6F7F63] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded"
            style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 300 }}
          >
            {t("detail.back")}
          </Link>
          <span className="text-[#E6E0D7]" aria-hidden="true">/</span>
          <Link
            to="/"
            className="text-[#D8D1C7] hover:text-[#6F7F63] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded"
            style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 300 }}
          >
            {product.category}
          </Link>
          <span className="text-[#E6E0D7]" aria-hidden="true">/</span>
          <span
            className="text-[#6F675F]"
            style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 400 }}
            aria-current="page"
          >
            {localized(product, "name", lang)}
          </span>
        </nav>
      </div>

      {/* Product */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Gallery */}
          <div className="space-y-4">
            <div className="relative overflow-hidden rounded-xl bg-[#F2EEE8] aspect-square">
              <ImageWithFallback
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
              {product.tag && (
                <span
                  className={`absolute top-4 left-4 px-4 py-1.5 rounded-full tracking-[0.2em] uppercase ${tagStyles[product.tag]}`}
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "10px", fontWeight: 400 }}
                >
                  {product.tag}
                </span>
              )}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`relative overflow-hidden rounded-lg bg-[#F2EEE8] aspect-square transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] ${
                    selectedImage === idx
                      ? "ring-2 ring-[#C7A76C] ring-offset-2 ring-offset-[#F7F5F1]"
                      : "opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <ImageWithFallback
                    src={img}
                    alt={`${product.name} ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="lg:py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p
                  className="text-[#C7A76C] tracking-[0.3em] uppercase mb-3"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
                >
                  {product.category}
                </p>
                <h1
                  className="text-[#2D2A26] tracking-[0.08em] mb-4"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "clamp(28px, 4vw, 42px)",
                    fontWeight: 400,
                  }}
                >
                  {localized(product, "name", lang)}
                </h1>
              </div>
              {/* Wishlist (only when feature enabled) */}
              {wishlistEnabled && (
                <button
                  onClick={handleWishlistToggle}
                  className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] ${
                    isWishlisted(product.id)
                      ? "bg-[#8A6D63] text-white"
                      : "bg-[#F2EEE8] text-[#6F675F] hover:bg-[#8A6D63] hover:text-white"
                  }`}
                  aria-label={isWishlisted(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <Heart size={18} className={isWishlisted(product.id) ? "fill-white" : ""} />
                </button>
              )}
            </div>

            <p
              className="text-[#C7A76C] mb-4"
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "28px", fontWeight: 400 }}
            >
              &euro;{product.price}
            </p>

            {/* Stock indicator */}
            {isOutOfStock && (
              <p className="text-[#8B5C58] mb-4" style={{ fontFamily: "'Jost', sans-serif", fontSize: "13px" }}>
                {t("detail.out_of_stock")}
              </p>
            )}
            {isLowStock && (
              <p className="text-[#C7A76C] mb-4" style={{ fontFamily: "'Jost', sans-serif", fontSize: "13px" }}>
                {product.stock_quantity} {t("detail.only_left")}
              </p>
            )}

            <p
              className="text-[#6F675F] leading-[1.9] mb-10"
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "15px", fontWeight: 300 }}
            >
              {localized(product, "description", lang)}
            </p>

            {/* Quantity + Add to Cart */}
            <div className="flex items-center gap-4 mb-5">
              <div className="flex items-center border border-[#E6E0D7] rounded-lg">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-11 h-11 flex items-center justify-center text-[#6F675F] hover:text-[#2D2A26] transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#C7A76C] rounded-l-lg"
                  disabled={isOutOfStock}
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span
                  className="w-10 text-center text-[#2D2A26]"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px" }}
                  aria-label={`Quantity: ${qty}`}
                >
                  {qty}
                </span>
                <button
                  onClick={() => setQty(Math.min(product.stock_quantity || 99, qty + 1))}
                  className="w-11 h-11 flex items-center justify-center text-[#6F675F] hover:text-[#2D2A26] transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#C7A76C] rounded-r-lg"
                  disabled={isOutOfStock}
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>

              <button
                onClick={handleAdd}
                disabled={isOutOfStock}
                className="flex-1 py-3 bg-[#6F7F63] text-white tracking-[0.25em] uppercase rounded-lg hover:bg-[#5F6F53] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#6F7F63] focus:ring-offset-2"
                style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 400 }}
              >
                <ShoppingBag size={16} />
                {isOutOfStock ? t("products.sold_out") : t("products.add_to_cart")}
              </button>
            </div>

            {/* Details */}
            <div className="border-t border-[#E6E0D7] pt-8">
              <p
                className="text-[#C7A76C] tracking-[0.3em] uppercase mb-5"
                style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
              >
                {t("detail.details")}
              </p>
              <div className="space-y-3">
                {product.details.map((detail) => (
                  <div key={detail.label} className="flex items-start gap-4">
                    <span
                      className="text-[#D8D1C7] w-28 shrink-0 tracking-[0.1em] uppercase"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px", fontWeight: 400 }}
                    >
                      {detail.label}
                    </span>
                    <span
                      className="text-[#2D2A26]"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px", fontWeight: 300 }}
                    >
                      {detail.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <ReviewSection productId={product.id} />

      {/* Related Products */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 mt-24">
          <div className="text-center mb-12">
            <p
              className="text-[#C7A76C] tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
            >
              {t("detail.related")}
            </p>
            <h2
              className="text-[#2D2A26] tracking-[0.1em]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(24px, 3vw, 34px)",
                fontWeight: 400,
              }}
            >
              {t("detail.related_title")}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                className="group"
              >
                <div className="relative overflow-hidden rounded-xl mb-5 bg-[#F2EEE8]">
                  <ImageWithFallback
                    src={p.images[0]}
                    alt={p.name}
                    className="w-full aspect-[3/4] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                  />
                  {p.tag && (
                    <span
                      className={`absolute top-3 left-3 px-3 py-1 rounded-full tracking-[0.15em] uppercase ${tagStyles[p.tag]}`}
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "10px", fontWeight: 400 }}
                    >
                      {p.tag}
                    </span>
                  )}
                </div>
                <h3
                  className="text-[#2D2A26] tracking-[0.08em] mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px", fontWeight: 500 }}
                >
                  {localized(p, "name", lang)}
                </h3>
                <p
                  className="text-[#6F675F] tracking-[0.1em]"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px", fontWeight: 300 }}
                >
                  &euro;{p.price}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recently Viewed */}
      <RecentlyViewedSection currentProductId={product.id} />
    </div>
  );
}